import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { LuFilter } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";

import Button from "../../components/Button/Button";
import FilterApplied from "../../components/FilterApplied/FilterApplied";
import Input from "../../components/Input/Input";
import ModalComponent from "../../components/Modal/ModalComponent";
import Select from "../../components/Select/Select";
import Container from "../../components/Container/Container";
import SearchInput from "../../components/SearchInput/SearchInput";

import { useListManager } from "../../contexts/list.manager";
import { useUserStore } from "../../contexts/userStore";
import EventsService from "../../services/EventsService";
import { debounce } from "../../utils/timer";
import { formatDate, createDateFromFormat } from "../../utils/date";

import { useTranslation } from "react-i18next";

import "./FilterLogs.css";
import { LogOperationKey, LogOperationText } from "../../constants/logs";
import { formatBytes } from "../../utils/bytes";

/**
 * Campos disponíveis nos Logs.
 *
 * Correspondem aos campos utilizados no componente de detalhes:
 *
 * id
 * user_name
 * user_email
 * user_type
 * date
 * url_requested
 * operation
 * log_data
 * entityType
 */
const FILTER_KEYS = [
   "search",
   "id",
   "user_name",
   "user_email",
   "user_type",
   "startDate",
   "endDate",
   "startTime",
   "endTime",
   "url_requested",
   "operation",
   "data",
   "entityType",
];

/**
 * Constrói os parâmetros da URL somente com os filtros preenchidos.
 */
function buildSearchParams(filters) {
   const params = new URLSearchParams();

   for (const key of FILTER_KEYS) {
      const value = filters[key];

      if (value != null && value !== "") {
         params.set(key, value);
      }
   }

   return params;
}

const FilterLogs = (props) => {
   const [searchParams, setSearchParams] = useSearchParams();
   const initializedRef = useRef(false);

   const [modalIsOpen, setModalIsOpen] = useState(false);

   const [search, setSearch] = useState(null);
   const [id, setId] = useState("");
   const [userName, setUserName] = useState("");
   const [userEmail, setUserEmail] = useState("");
   const [userType, setUserType] = useState("");

   const [startDate, setStartDate] = useState("");
   const [endDate, setEndDate] = useState("");
   const [startTime, setStartTime] = useState("");
   const [endTime, setEndTime] = useState("");

   const [urlRequested, setUrlRequested] = useState("");
   const [operation, setOperation] = useState("");
   const [data, setData] = useState("");
   const [entityType, setEntityType] = useState("");

   const [showFiltersApplied, setShowFiltersApplied] = useState(false);

   const { closeForm } = useListManager();
   const { selectedCondominio } = useUserStore();
   const { t } = useTranslation();

   // ---------------------------------------------------------------------------
   // Inicialização dos filtros a partir da URL
   // ---------------------------------------------------------------------------

   useEffect(() => {
      if (initializedRef.current) return;

      initializedRef.current = true;

      const sp = searchParams;

      const hasAny = FILTER_KEYS.some((key) => sp.has(key));

      if (!hasAny) return;

      const urlSearch = sp.get("search") || "";
      const urlId = sp.get("id") || "";
      const urlUserName = sp.get("user_name") || "";
      const urlUserEmail = sp.get("user_email") || "";
      const urlUserType = sp.get("user_type") || "";

      const urlStartDate = sp.get("startDate") || "";
      const urlEndDate = sp.get("endDate") || "";
      const urlStartTime = sp.get("startTime") || "";
      const urlEndTime = sp.get("endTime") || "";

      const urlRequested = sp.get("url_requested") || "";
      const urlOperation = sp.get("operation") || "";
      const urlData = sp.get("data") || "";
      const urlEntityType = sp.get("entityType") || "";

      setSearch(urlSearch);
      setId(urlId);
      setUserName(urlUserName);
      setUserEmail(urlUserEmail);
      setUserType(urlUserType);

      setStartDate(urlStartDate);
      setEndDate(urlEndDate);
      setStartTime(urlStartTime);
      setEndTime(urlEndTime);

      setUrlRequested(urlRequested);
      setOperation(urlOperation);
      setData(urlData);
      setEntityType(urlEntityType);

      setShowFiltersApplied(true);

      props.filtersApplied({
         search: urlSearch,
         id: urlId,
         user_name: urlUserName,
         user_email: urlUserEmail,
         user_type: urlUserType,
         startDate: urlStartDate,
         endDate: urlEndDate,
         startTime: urlStartTime,
         endTime: urlEndTime,
         url_requested: urlRequested,
         operation: urlOperation,
         data: urlData,
         entityType: urlEntityType,
      });
   }, []); // eslint-disable-line react-hooks/exhaustive-deps

   // ---------------------------------------------------------------------------
   // Sincronização dos filtros com a URL
   // ---------------------------------------------------------------------------

   const syncFiltersToUrl = useCallback(
      (filters) => {
         setSearchParams(buildSearchParams(filters), {
            replace: true,
         });
      },
      [setSearchParams]
   );

   // ---------------------------------------------------------------------------
   // Monta o objeto atual de filtros
   // ---------------------------------------------------------------------------

   const getFilters = useCallback(
      (searchValue = search) => ({
         search: searchValue,
         id,
         user_name: userName,
         user_email: userEmail,
         user_type: userType,
         startDate,
         endDate,
         startTime,
         endTime,
         url_requested: urlRequested,
         operation,
         data,
         entityType,
      }),
      [
         search,
         id,
         userName,
         userEmail,
         userType,
         startDate,
         endDate,
         startTime,
         endTime,
         urlRequested,
         operation,
         data,
         entityType,
      ]
   );

   // ---------------------------------------------------------------------------
   // Limpa todos os filtros
   // ---------------------------------------------------------------------------

   const clearFilters = () => {
      const emptyFilters = {
         search: "",
         id: "",
         user_name: "",
         user_email: "",
         user_type: "",
         startDate: "",
         endDate: "",
         startTime: "",
         endTime: "",
         url_requested: "",
         operation: "",
         data: "",
         entityType: "",
      };

      setSearch("");
      setId("");
      setUserName("");
      setUserEmail("");
      setUserType("");

      setStartDate("");
      setEndDate("");
      setStartTime("");
      setEndTime("");

      setUrlRequested("");
      setOperation("");
      setData("");
      setEntityType("");

      setShowFiltersApplied(false);

      closeForm();

      props.filtersApplied(emptyFilters);

      syncFiltersToUrl(emptyFilters);
   };

   // ---------------------------------------------------------------------------
   // Confirmação dos filtros
   // ---------------------------------------------------------------------------

   const handleConfirm = (searchChanged = search) => {
      const filters = getFilters(searchChanged);

      setShowFiltersApplied(true);
      setModalIsOpen(false);

      closeForm();

      props.filtersApplied(filters);

      syncFiltersToUrl(filters);
   };

   const handleCancel = () => {
      setModalIsOpen(false);
   };

   // ---------------------------------------------------------------------------
   // Exportação CSV
   // ---------------------------------------------------------------------------

   // ---------------------------------------------------------------------------
   // Busca com debounce
   // ---------------------------------------------------------------------------

   const changeSearchDebounce = useCallback(
      debounce((searchValue) => {
         handleConfirm(searchValue);
      }, 700),
      [
         id,
         userName,
         userEmail,
         userType,
         startDate,
         endDate,
         startTime,
         endTime,
         urlRequested,
         operation,
         data,
         entityType,
      ]
   );

   useEffect(() => {
      if (search == null) return;

      changeSearchDebounce(search);
   }, [search]);

   // ---------------------------------------------------------------------------
   // Remove um filtro individual
   // ---------------------------------------------------------------------------

   const removeFilter = (filter) => {
      const updatedFilters = {
         search: filter === "search" ? "" : search,
         id: filter === "id" ? "" : id,
         user_name: filter === "user_name" ? "" : userName,
         user_email: filter === "user_email" ? "" : userEmail,
         user_type: filter === "user_type" ? "" : userType,

         startDate: filter === "startDate" ? "" : startDate,
         endDate: filter === "endDate" ? "" : endDate,
         startTime: filter === "startTime" ? "" : startTime,
         endTime: filter === "endTime" ? "" : endTime,

         url_requested: filter === "url_requested" ? "" : urlRequested,
         operation: filter === "operation" ? "" : operation,
         data: filter === "data" ? "" : data,
         entityType: filter === "entityType" ? "" : entityType,
      };

      if (filter === "search") setSearch("");
      if (filter === "id") setId("");
      if (filter === "user_name") setUserName("");
      if (filter === "user_email") setUserEmail("");
      if (filter === "user_type") setUserType("");

      if (filter === "startDate") setStartDate("");
      if (filter === "endDate") setEndDate("");
      if (filter === "startTime") setStartTime("");
      if (filter === "endTime") setEndTime("");

      if (filter === "url_requested") setUrlRequested("");
      if (filter === "operation") setOperation("");
      if (filter === "data") setData("");
      if (filter === "entityType") setEntityType("");

      props.filtersApplied(updatedFilters);
      syncFiltersToUrl(updatedFilters);
   };

   return (
      <Container className="pt-0!">
         {/* ---------------------------------------------------------------- */}
         {/* Barra de ações                                                   */}
         {/* ---------------------------------------------------------------- */}

         <div className="logs-filters-buttons-container px-0! mx-0! mt-0! justify-between!">
            <SearchInput
               value={search}
               placeholder={t("common.search")}
               onChange={({ target }) => setSearch(target.value)}
            />

            <div className="flex flex-row-reverse items-center gap-2.5">
               <Button
                  className="button-blue logs-filters-button"
                  label={t("common.advancedFilters")}
                  icon={<LuFilter size={18} />}
                  onClick={() => setModalIsOpen(true)}
               />

               <Button
                  className="button-white logs-filters-button"
                  label={t("common.clearFilters")}
                  icon={<MdOutlineDelete size={17} />}
                  onClick={clearFilters}
               />

               <Button
                  className="button-white logs-filters-button"
                  label={props.isExporting
                     ? `Recebendo CSV (${formatBytes(props.exportedBytes)})`
                     : t("common.exportCSV")}
                  icon={<FiDownload size={17} />}
                  onClick={props.onDownloadCsv}
                  disabled={props.isExporting}
               />
            </div>
         </div>

         {/* ---------------------------------------------------------------- */}
         {/* Filtros aplicados                                                 */}
         {/* ---------------------------------------------------------------- */}

         {showFiltersApplied && (
            <div className="logs-filters-applied-container">
               <FilterApplied
                  filter={search}
                  removeFilter={() => removeFilter("search")}
               />

               <FilterApplied
                  filter={id}
                  removeFilter={() => removeFilter("id")}
               />

               <FilterApplied
                  filter={userName}
                  removeFilter={() => removeFilter("user_name")}
               />

               <FilterApplied
                  filter={userEmail}
                  removeFilter={() => removeFilter("user_email")}
               />

               <FilterApplied
                  filter={userType}
                  removeFilter={() => removeFilter("user_type")}
               />

               <FilterApplied
                  filter={
                     startDate
                        ? formatDate(
                           createDateFromFormat("Y-m-d", startDate)
                        )
                        : null
                  }
                  removeFilter={() => removeFilter("startDate")}
               />

               <FilterApplied
                  filter={
                     endDate
                        ? formatDate(
                           createDateFromFormat("Y-m-d", endDate)
                        )
                        : null
                  }
                  removeFilter={() => removeFilter("endDate")}
               />

               <FilterApplied
                  filter={startTime}
                  removeFilter={() => removeFilter("startTime")}
               />

               <FilterApplied
                  filter={endTime}
                  removeFilter={() => removeFilter("endTime")}
               />

               <FilterApplied
                  filter={urlRequested}
                  removeFilter={() => removeFilter("url_requested")}
               />

               <FilterApplied
                  filter={operation}
                  removeFilter={() => removeFilter("operation")}
               />

               <FilterApplied
                  filter={data}
                  removeFilter={() => removeFilter("data")}
               />

               <FilterApplied
                  filter={entityType}
                  removeFilter={() => removeFilter("entityType")}
               />
            </div>
         )}

         {/* ---------------------------------------------------------------- */}
         {/* Modal                                                             */}
         {/* ---------------------------------------------------------------- */}

         <ModalComponent
            modalIsOpen={modalIsOpen}
            modalText={t("common.advancedFilters")}
            handleConfirm={() => handleConfirm(search)}
            handleCancel={handleCancel}
         >
            <div className="logs-filters-modal-inputs-container">

               {/* ========================================================== */}
               {/* Usuário                                                     */}
               {/* ========================================================== */}

               <div className="filters-group mb-3!">
                  <h3 className="filters-group-title">
                     {t("common.user")}
                  </h3>

                  <div className="input-group">
                     <Input
                        value={id}
                        labelText="Id"
                        onChange={({ target }) => setId(target.value)}
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        value={userName}
                        labelText={t("common.name")}
                        onChange={({ target }) => setUserName(target.value)}
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        value={userEmail}
                        labelText="Email"
                        type="email"
                        onChange={({ target }) =>
                           setUserEmail(target.value)
                        }
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        value={userType}
                        labelText={`${t("common.type")} / ${t(
                           "common.profile"
                        )}`}
                        onChange={({ target }) =>
                           setUserType(target.value)
                        }
                        className="input-form"
                     />
                  </div>
               </div>

               {/* ========================================================== */}
               {/* Data / Hora                                                 */}
               {/* ========================================================== */}

               <div className="filters-group group-date-range mb-3.5!">
                  <h3 className="filters-group-title">
                     {t("common.timeInterval")}
                  </h3>

                  <div className="input-group">
                     <Input
                        value={startDate}
                        type="date"
                        labelText={t("common.startDate")}
                        onChange={({ target }) =>
                           setStartDate(target.value)
                        }
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        value={endDate}
                        type="date"
                        labelText={t("common.endDate")}
                        onChange={({ target }) =>
                           setEndDate(target.value)
                        }
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        name="start_hour"
                        id="start_hour"
                        type="time"
                        min="00:00"
                        max="23:59"
                        labelText={t("common.startTime")}
                        value={startTime}
                        onChange={({ target }) =>
                           setStartTime(target.value)
                        }
                        required={false}
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        name="end_hour"
                        id="end_hour"
                        type="time"
                        min="00:00"
                        max="23:59"
                        labelText={t("common.endTime")}
                        value={endTime}
                        onChange={({ target }) =>
                           setEndTime(target.value)
                        }
                        required={false}
                     />
                  </div>
               </div>

               {/* ========================================================== */}
               {/* Log                                                          */}
               {/* ========================================================== */}

               <div className="filters-group mb-3!">
                  <h3 className="filters-group-title">
                     Log
                  </h3>

                  <div className="input-group">
                     <Input
                        value={urlRequested}
                        labelText="Url"
                        onChange={({ target }) =>
                           setUrlRequested(target.value)
                        }
                        className="input-form"
                     />
                  </div>

                  <div className="input-group">
                     <Select
                        labelText={t("common.operation")}
                        name="operation"
                        id="operation"
                        value={operation}
                        onChange={({ target }) => setOperation(target.value)}
                        options={[
                           { value: LogOperationKey.CREATE, text: LogOperationText[LogOperationKey.CREATE] },
                           { value: LogOperationKey.EDIT, text: LogOperationText[LogOperationKey.EDIT] },
                           { value: LogOperationKey.DELETE, text: LogOperationText[LogOperationKey.DELETE] },
                        ]}
                     />
                  </div>

                  <div className="input-group">
                     <Input
                        value={data}
                        labelText={t("common.data")}
                        onChange={({ target }) =>
                           setData(target.value)
                        }
                        className="input-form"
                     />
                  </div>
               </div>
            </div>
         </ModalComponent>
      </Container>
   );
};

export default FilterLogs;
